import React, { useState, useEffect } from 'react';
import userService, { User } from '../../services/userService';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';

interface UserFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

interface FormData {
  name: string;
  email: string;
  role: 'admin' | 'auditor' | 'manager';
  password?: string;
}

const UserForm: React.FC<UserFormProps> = ({ open, onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    role: 'auditor',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || '',
        role: user.role || 'auditor',
        password: '' // Siempre inicializar password
      });
    } else {
      setFormData({
        name: '',
        email: '',
        role: 'auditor',
        password: ''
      });
    }
    setError(null);
    setErrors({});
  }, [user, open]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!user && !formData.password) {
      newErrors.password = 'La contraseña es requerida';
    } else if (!user && formData.password && formData.password.length < 6) {
      newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      if (user) {
        // Editar usuario
        await userService.update(user.id, {
          name: formData.name,
          email: formData.email,
          role: formData.role
        });
      } else {
        // Crear usuario
        await userService.create({
          name: formData.name,
          email: formData.email,
          role: formData.role,
          password: formData.password || ''
        });
      }

      onSuccess();
    } catch (err: any) {
      console.error('Error saving user:', err);
      setError(err.response?.data?.error || 'Error al guardar usuario');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof FormData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    // Limpiar error del campo al escribir
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          {user ? 'Editar Usuario' : 'Nuevo Usuario'}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <TextField
              label="Nombre Completo"
              value={formData.name || ''}
              onChange={handleChange('name')}
              error={!!errors.nombre}
              helperText={errors.nombre}
              required
              fullWidth
              autoFocus
            />

            <TextField
              label="Email"
              type="email"
              value={formData.email || ''}
              onChange={handleChange('email')}
              error={!!errors.email}
              helperText={errors.email}
              required
              fullWidth
            />

            <TextField
              label="Rol"
              select
              value={formData.role || 'auditor'}
              onChange={handleChange('role')}
              required
              fullWidth
            >
              <MenuItem value="admin">Administrador</MenuItem>
              <MenuItem value="auditor">Auditor</MenuItem>
              <MenuItem value="manager">Gerente</MenuItem>
            </TextField>

            {!user && (
              <TextField
                label="Contraseña"
                type="password"
                value={formData.password || ''}
                onChange={handleChange('password')}
                error={!!errors.password}
                helperText={errors.password || 'Mínimo 6 caracteres'}
                required
                fullWidth
              />
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading}
            startIcon={loading && <CircularProgress size={20} />}
          >
            {user ? 'Guardar' : 'Crear'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserForm;
